import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplyDiscount,
  CreatePromoCode,
  Discount,
  DiscountReasonCode,
  DiscountReport,
  ListDiscountsQuery,
  PromoCode,
  PromoResolution,
  UpdatePromoCode,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS9 phase 3 — discounts & promo codes (brief 09 §3). TanStack Query over the generated client,
 * mirroring data/services.ts / data/policy.ts. TWO distinct things, kept apart exactly as the types do:
 *
 *  1. A checkout DISCOUNT (till): a percent/fixed reduction that ALWAYS carries an ACTOR (server-
 *     resolved) + a REASON CODE — no silent price overrides (brief non-negotiable). The form enforces
 *     the reason code (and the free-text note when reasonCode = 'other'); apply is idempotent on a
 *     client `idempotencyKey`, so the till gates the button on `isPending` and a double-tap is a no-op.
 *  2. A PROMO CODE for online booking (WS8): manager-defined CRUD here; the PUBLIC, no-login resolve
 *     (`useResolvePromoCode`) is what the booking surface calls to show the deterministic, shown-before-
 *     commitment reduction — it never touches the WS8 frozen-policy contract.
 *
 * A "£X off" pure-discount voucher is a discount LINE, never a voucher_ledger posting (ADR-0008 §4) —
 * that boundary lives in the types; this layer just POSTs the apply-discount intent.
 */

export const DISCOUNTS_PAGE = 50;
export const PROMO_CODES_PAGE = 50;

export interface DiscountPage {
  data: Discount[];
  pagination: { limit: number; offset: number; total: number };
}

export interface PromoCodePage {
  data: PromoCode[];
  pagination: { limit: number; offset: number; total: number };
}

// ── Checkout discounts (the audit trail) ────────────────────────────────────────────

/**
 * Apply a discount at checkout. The actor is the authenticated staff member (server-resolved, never
 * client-supplied); the body carries the reason code (+ note when 'other'). Idempotent on
 * `idempotencyKey`: the caller mints it once per intent and gates the button on `isPending`.
 */
export function useApplyDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ApplyDiscount): Promise<Discount> =>
      unwrap(await api.POST('/v1/discounts', { body: body as never })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['discounts'] });
    },
  });
}

/** The append-only discount audit trail (reportable: filter by reason code / payment / window). */
export function useDiscounts(query: Omit<ListDiscountsQuery, 'limit' | 'offset'> = {}, offset = 0) {
  return useQuery({
    queryKey: qk.discounts(query, offset),
    queryFn: async (): Promise<DiscountPage> =>
      unwrap(
        await api.GET('/v1/discounts', {
          params: { query: { ...query, limit: DISCOUNTS_PAGE, offset } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * The discount-reason report (where margin is going): total + count broken out by reason code over an
 * optional window. Manager-only on the API.
 */
export function useDiscountReport(range: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: qk.discountReport(range.from ?? null, range.to ?? null),
    enabled,
    queryFn: async (): Promise<DiscountReport> =>
      unwrap(
        await api.GET('/v1/discounts/report', {
          params: { query: { from: range.from, to: range.to } },
        }),
      ),
  });
}

// ── Promo codes (online booking) ─────────────────────────────────────────────────────

export function usePromoCodes(active?: boolean, offset = 0) {
  return useQuery({
    queryKey: qk.promoCodes(active ?? null, offset),
    queryFn: async (): Promise<PromoCodePage> =>
      unwrap(
        await api.GET('/v1/promo-codes', {
          params: { query: { active, limit: PROMO_CODES_PAGE, offset } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useCreatePromoCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePromoCode): Promise<PromoCode> =>
      unwrap(await api.POST('/v1/promo-codes', { body: body as never })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['promo-codes'] });
    },
  });
}

export function useUpdatePromoCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: UpdatePromoCode }): Promise<PromoCode> =>
      unwrap(
        await api.PATCH('/v1/promo-codes/{id}', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['promo-codes'] });
    },
  });
}

/**
 * Public, no-login promo-code validation for the online-booking surface (WS8). Resolves a code against
 * a (service, base price) and returns the deterministic, shown-before-commitment reduction (`valid`
 * + `discountPence`/`netPence`), or the disqualifying `reason` when not valid. Gated on a non-empty
 * code so an empty field doesn't probe the rate-limited endpoint.
 */
export function useResolvePromoCode(
  args: { slug: string; code: string; serviceId?: string; basePence: number } | null,
  enabled = true,
) {
  return useQuery({
    queryKey: qk.promoResolve(
      args?.slug ?? '',
      args?.code ?? '',
      args?.serviceId,
      args?.basePence ?? 0,
    ),
    enabled: Boolean(args?.slug && args?.code) && enabled,
    queryFn: async (): Promise<PromoResolution> =>
      unwrap(
        await api.GET('/public/promo/{slug}/{code}', {
          params: {
            path: { slug: args!.slug, code: args!.code },
            query: { serviceId: args!.serviceId, basePence: args!.basePence },
          },
        }),
      ),
  });
}

// Re-exported for the till's reason picker (the canonical reportable set lives in @salonomy/types).
export type { DiscountReasonCode };
